import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';

import OnboardingItemFabrica from '../OnboardingItemFabrica/OnboardingItemFabrica';
import { Button, Divider, Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Delete } from '../../../assets/delete-black-icon.svg';
import { ReactComponent as Plus } from '../../../assets/pluse-black-icon.svg';

import {
  createCompositeInputElement,
  deleteCompositeInputElement,
  editCompositeInputElement,
  getOnboardingById,
} from '../../../data/store/onboarding/onboardingActions';
import { getValueObj } from '../OnboardingCategory';

const isEmptyValue = (value) => {
  if (value === undefined || value === null || value === '') return true;
  if (dayjs.isDayjs(value)) return !value.isValid();
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') {
    return Object.keys(value).length === 0;
  }
  return false;
};

const isRequiredSubField = (item) => item?.form_input_element?.min_number_of_submissions === 1;

const OnboardingCompositeItemFabrica = ({ formItem }) => {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const dispatch = useDispatch();
  const { onboardingById } = useSelector((state) => state.onboardingReducer);

  const onboardingId = onboardingById?.id;
  const compositeElementId = formItem?.form_composite_input_element?.id;
  const data = formItem?.form_composite_input_element;

  const multiple = formItem?.form_composite_input_element?.max_number_of_submissions === null;

  const sortedItems = useMemo(
    () =>
      data?.form_composite_input_element_items
        ?.slice()
        ?.sort((a, b) => a?.element_order - b?.element_order),
    [data?.form_composite_input_element_items],
  );

  const createInFlightRef = useRef(false);

  useEffect(() => {
    createInFlightRef.current = false;
  }, [onboardingById?.form_composite_input_element_submissions]);

  const getRowValues = useCallback(
    (listIndex) => {
      if (listIndex === undefined) {
        return form.getFieldValue(compositeElementId) ?? {};
      }
      return form.getFieldValue(compositeElementId)?.[listIndex] ?? {};
    },
    [compositeElementId, form],
  );

  /** Валидация только обязательных и уже заполненных полей (без пустых необязательных) — перед POST */
  const collectValidatePathsForSubmit = useCallback(
    (listIndex, rowObj) => {
      const paths = [];
      const rowVals = rowObj ?? getRowValues(listIndex);

      sortedItems?.forEach((schemaItem) => {
        const el = schemaItem?.form_input_element;
        const fid = el?.id;
        if (!fid) return;

        const shouldValidate =
          isRequiredSubField(schemaItem) || !isEmptyValue(rowVals?.[fid]);

        if (!shouldValidate) return;

        const base =
          listIndex === undefined
            ? [compositeElementId, fid]
            : [compositeElementId, listIndex, fid];

        const listFixedMulti =
          el?.type === 'LIST_FIXED' &&
          (!Object.hasOwn(el, 'max_number_of_submissions') ||
            el.max_number_of_submissions === null);

        if (listFixedMulti) {
          (schemaItem?.options?.options ?? []).forEach((opt) => {
            paths.push([...base, opt]);
          });
          return;
        }

        const emailMulti =
          el?.type === 'EMAIL' &&
          (el.max_number_of_submissions === null ||
            el.max_number_of_submissions !== 1 ||
            !Object.hasOwn(el, 'max_number_of_submissions'));

        if (emailMulti) {
          const entries = rowVals?.[fid];
          const n = Array.isArray(entries) ? entries.length : 0;
          for (let i = 0; i < n; i += 1) {
            paths.push([...base, i, 'value_text']);
          }
          return;
        }

        paths.push(base);
      });

      return paths;
    },
    [compositeElementId, getRowValues, sortedItems],
  );

  const buildPayload = useCallback(
    (rowObj) => {
      const obj = {
        form_composite_input_element_id: compositeElementId,
        form_submission_id: onboardingId,
        form_input_element_submissions: [],
      };

      sortedItems?.forEach((schemaItem) => {
        const inputEl = schemaItem?.form_input_element;
        const fieldId = inputEl?.id;
        if (!fieldId) return;

        const fieldValue = rowObj?.[fieldId];
        if (
          fieldValue === undefined ||
          fieldValue === null ||
          fieldValue === ''
        ) {
          return;
        }

        obj.form_input_element_submissions.push({
          form_input_element_id: fieldId,
          form_submission_id: onboardingId,
          ...getValueObj(inputEl, fieldValue),
        });
      });

      return obj;
    },
    [compositeElementId, onboardingId, sortedItems],
  );

  const handleCompositeBlur = useCallback(
    async (listIndex, blurFieldPath) => {
      if (!compositeElementId || !onboardingId || !sortedItems?.length || !form) {
        return;
      }

      try {
        if (blurFieldPath != null) {
          await form.validateFields([blurFieldPath]);
        }
      } catch {
        return;
      }

      const rowObj = getRowValues(listIndex);

      const existingSingle =
        onboardingById?.form_composite_input_element_submissions?.find(
          (s) => s.form_composite_input_element_id === compositeElementId,
        );

      const rowId =
        listIndex === undefined ? existingSingle?.id : rowObj?.id;

      const payload = buildPayload(rowObj);

      if (!rowId) {
        const allRequiredFilled = sortedItems.every((si) => {
          if (!isRequiredSubField(si)) return true;
          const fid = si?.form_input_element?.id;
          return fid ? !isEmptyValue(rowObj[fid]) : true;
        });

        if (!allRequiredFilled) return;

        const submitPaths = collectValidatePathsForSubmit(listIndex, rowObj);
        try {
          if (submitPaths.length) {
            await form.validateFields(submitPaths);
          }
        } catch {
          return;
        }

        if (createInFlightRef.current) return;
        createInFlightRef.current = true;

        dispatch(createCompositeInputElement(payload));
        window.setTimeout(() => {
          createInFlightRef.current = false;
        }, 8000);
        return;
      }

      dispatch(editCompositeInputElement({ id: rowId, ...payload }));
    },
    [
      buildPayload,
      collectValidatePathsForSubmit,
      compositeElementId,
      dispatch,
      form,
      getRowValues,
      onboardingById?.form_composite_input_element_submissions,
      onboardingId,
      sortedItems,
    ],
  );

  const handleRemoveListRow = useCallback(
    (listIndex, remove) => {
      const row = form.getFieldValue(compositeElementId)?.[listIndex];
      const rowId = row?.id;

      if (rowId) {
        dispatch(deleteCompositeInputElement(rowId));
        onboardingId && dispatch(getOnboardingById(onboardingId));
      } else {
        remove(listIndex);
      }
    },
    [compositeElementId, dispatch, form, onboardingId],
  );

  return (
    <div>
      <Divider
        className="filter-divider onboarding-composite-divider"
        variant="dashed"
      >
        {data?.description}
      </Divider>
      {multiple ? (
        <Form.List name={data?.id}>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name: listFieldName }) => (
                <div key={key}>
                  {sortedItems?.map((item) => (
                    <div
                      key={`${key}-${item?.element_order}`}
                      style={{ marginBottom: '24px' }}
                    >
                      <OnboardingItemFabrica
                        form={form}
                        formItem={item}
                        onboardingId={onboardingId}
                        composite={data?.id}
                        multiName={listFieldName}
                        deferRemoteSubmit
                        onDeferredBlur={(path) =>
                          handleCompositeBlur(listFieldName, path)
                        }
                      />
                    </div>
                  ))}
                  <div className="onboarding-delete-btn-wrapper">
                    <Delete
                      className="onboarding-delete-btn"
                      onClick={() =>
                        handleRemoveListRow(listFieldName, remove)
                      }
                    />
                  </div>
                </div>
              ))}
              <div>
                <Button
                  type="text"
                  icon={<Plus />}
                  iconPosition="end"
                  className="profile-data-addbutton"
                  onClick={() => add()}
                >
                  {t('ADD')}
                </Button>
              </div>
            </>
          )}
        </Form.List>
      ) : (
        <>
          {sortedItems?.map((item) => (
            <div key={item?.element_order} style={{ marginBottom: '24px' }}>
              <OnboardingItemFabrica
                form={form}
                formItem={item}
                onboardingId={onboardingId}
                composite={data?.id}
                deferRemoteSubmit
                onDeferredBlur={(path) =>
                  handleCompositeBlur(undefined, path)
                }
              />
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default React.memo(OnboardingCompositeItemFabrica);

OnboardingCompositeItemFabrica.propTypes = {
  formItem: PropTypes.object.isRequired,
};
